import React, { useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { Button, TextField, Stack, IconButton, MenuItem, SelectField, Icons, DateTimeStandard } from '@curves/ui-base';
import { useSnackbar } from "notistack";
import useClubInventoryApi from "@apis/useClubInventoryApi";
import useClubStockOutApi from "@apis/useClubStockOutApi";
import { GradientColorHeader, ReadTextField, ProductInfo } from '@curves/ui-shared';
// TODO: 改為庫存與 icoach 共用的 EmployeeSelect 後統一匯入路徑
import { EmployeeSelect } from "@icoach/components";
import { SkuAddToList, InventoryProductList, InputGroup } from "@clubInventory/components/";
import { useTransferSkuAdd } from "@clubInventory/hooks/useSkuAddToList";
import { ExpectedQuantityCell, getExpectedQuantityCellClassName } from "@clubInventory/purchase/components/";
import useOptions from "@util/hook/useOptions";
import { callRef, isFunction, ensureArray, refIsRequiredError } from "@util/utils";
import { parseDate, getNowTime } from "@util/moment";

// 功能：店鋪調撥商品清單表頭定義（商品資訊 / 數量 / 刪除）
const storeTransferHeaderRow = [
	{
		cellKey: "productInfo",
		headerLabel: "商品資訊",
		minWidth: 200,
		formatCell: (_, row) => (
			<ProductInfo
				product={{
					sku: row.sku,
					name: row.productName,
					spec: row.variantName ?? "",
				}}
			/>
		),
	},
	{
		cellKey: "quantity",
		headerLabel: "數量",
		align: "center",
		minWidth: 120,
		getCellClassName: (quantity, row) => {
			const qty = quantity ?? row.quantity ?? 0;
			const stockQuantity = row?.stockQuantity ?? 0;
			return getExpectedQuantityCellClassName(qty, stockQuantity);
		},
		formatCell: (quantity, row, { onOpenQuantityEdit }) => {
			const qty = quantity ?? row.quantity ?? 0;
			const stockQuantity = row?.stockQuantity ?? 0;
			return <ExpectedQuantityCell actualQuantity={qty} expectedQuantity={stockQuantity} item={row} onOpenQuantityEdit={onOpenQuantityEdit} />;
		},
	},
	{
		cellKey: "actions",
		headerLabel: "",
		align: "center",
		minWidth: 80,
		formatCell: (_, row, { onDelete, getProductKey }) => {
			const productKey = getProductKey ? getProductKey(row) : `${row.productID}_${row.variantID ?? ""}`;
			return (
				<IconButton onClick={() => onDelete?.(productKey)} size="small">
					<Icons.Trash />
				</IconButton>
			);
		},
	},
];

// 功能：送出 API payload 的 item 映射（調撥單明細）
const toTransferItem = p => ({
	productID: p.productID,
	variantID: p.variantID ?? 0,
	quantity: Number(p.quantity) || 0,
});

// 功能：店鋪調撥單彈窗（掃碼加入商品、選擇調撥店鋪、送出建立出貨單）
const StoreTransferDialog = ({ open, onClose, refresh }) => {
	// 功能：表單欄位 ref（建單人、調撥店鋪、備註）
	const creatorRef = useRef(null);
	const storeRef = useRef(null);
	const reasonRef = useRef(null);
	const noteRef = useRef(null);

	const { enqueueSnackbar } = useSnackbar();
	const { getClubInventorySearchStockSingleApi } = useClubInventoryApi();
	const { getClubStockOutOptionsApi, postClubStockOutTransferApi } = useClubStockOutApi();
	const { options, getOptionsApi } = useOptions({
		getOptionsApi: getClubStockOutOptionsApi,
		isFetchOnMount: false,
	});

	// 功能：Dialog 開啟時載入店鋪出貨相關選項（建單人、調撥店鋪等）
	useEffect(() => {
		if (open && isFunction(getOptionsApi)) getOptionsApi(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const employeeSelectOptions = ensureArray(options?.employeeOptions);
	const transferStoreOptions = ensureArray(options?.clubOptions);

	// 功能：掃碼/輸入 SKU 加入商品，fetchProductBySku 依 SKU 精準查詢店鋪庫存單筆資料
	const skuAdd = useTransferSkuAdd({
		fetchProductBySku: async sku => {
			const match = await getClubInventorySearchStockSingleApi({ text: sku });
			if (!match) return null;
			return {
				productID: match.productID,
				productName: match.productName,
				variantID: match.variantID,
				variantName: match.variantName,
				sku: match.sku,
				pictureSrc: match.pictureSrc,
				quantity: 1,
				stockQuantity: match.stock,
			};
		},
		getProductKey: p => `${p.productID ?? ""}_${p.variantID ?? ""}`,
	});
	const { products: productList, clearProducts: clearProductList } = skuAdd;

	// 功能：關閉時清空商品列表並呼叫 onClose
	const handleClose = useCallback(
		e => {
			clearProductList();
			if (isFunction(onClose)) onClose(e);
		},
		[onClose, clearProductList],
	);

	// 功能：驗證必填與商品數量 → 組 payload 送出建立調撥單，成功則刷新並關閉
	const handleSubmit = async e => {
		e.preventDefault();

		const requiredRefs = [creatorRef, storeRef, noteRef];
		if (refIsRequiredError(...requiredRefs)) return;

		if (productList.length === 0) {
			enqueueSnackbar("請至少加入一個商品才能建立調撥單", { variant: "warning" });
			return;
		}
		const hasInvalidQty = productList.some(item => (Number(item.quantity) || 0) < 0);
		if (hasInvalidQty) {
			enqueueSnackbar("請確認所有商品數量，數量不可為負數", { variant: "warning" });
			return;
		}

		const payload = {
			stockOutDate: getNowTime(),
			targetClubID: callRef(storeRef, "getResult"),
			employeeID: Number(callRef(creatorRef, "getResult") || 0),
			reason: String(callRef(reasonRef, "getResult") || "").trim(),
			notes: String(callRef(noteRef, "getResult") || "").trim(),
			items: productList.map(toTransferItem),
		};

		const resp = await postClubStockOutTransferApi(payload);
		if (resp) {
			isFunction(refresh) && refresh();
			handleClose();
		}
	};

	if (!open) return null;

	return (
		<Dialog className="simple-content dialog" PaperProps={{ className: "wx-90rem" }} open={open} onClose={handleClose} fullWidth maxWidth="lg">
			<GradientColorHeader onClose={handleClose}>店鋪調撥單</GradientColorHeader>
			<DialogContent className="p-3">
				<Grid container spacing={3}>
					{/* 左 4：表單 */}
					<Grid item xs={12} md={4}>
						<Grid container spacing={2}>
							<Grid xs={12} item>
								<ReadTextField label="建單日期">{parseDate(new Date(), DateTimeStandard.DisplayDateBySlash)}</ReadTextField>
							</Grid>
							<Grid xs={12} item>
								<EmployeeSelect label="建單人" ref={creatorRef} options={employeeSelectOptions} fullWidth required />
							</Grid>
							<Grid xs={12} item>
								<SelectField label="調撥店鋪" ref={storeRef} placeholder="請選擇" fullWidth required>
									{transferStoreOptions.map(({ text, value, disabled }) => (
										<MenuItem key={value} value={value} disabled={disabled}>
											{text}
										</MenuItem>
									))}
								</SelectField>
							</Grid>
							<Grid xs={12} item>
								<TextField label="原因" ref={reasonRef} placeholder="請填寫原因" fullWidth />
							</Grid>
							<Grid xs={12} item>
								<TextField label="備註" ref={noteRef} placeholder="調撥店鋪看的備註" rows={2} multiline fullWidth />
							</Grid>
						</Grid>
					</Grid>
					{/* 右 6：SkuAddToList */}
					<Grid item xs={12} md={8}>
						<SkuAddToList {...skuAdd}>
							<Stack spacing={2} alignItems="center" className="mb-3">
								<InputGroup className="product-input-group">
									<InputGroup.StartAdornment>
										<SkuAddToList.ScanButton />
									</InputGroup.StartAdornment>
									<InputGroup.Input>
										<SkuAddToList.Input placeholder="請使用手持掃描器掃描或輸入商品SKU" />
									</InputGroup.Input>
								</InputGroup>
								<SkuAddToList.SubmitButton>加入商品</SkuAddToList.SubmitButton>
							</Stack>
							<SkuAddToList.ScannerDialog title="商品掃描" continuousScan />
							<SkuAddToList.ConfirmDialog />
							<SkuAddToList.List>
								<InventoryProductList headerRow={storeTransferHeaderRow} emptyMessage="請掃描商品條碼" height="360px" />
							</SkuAddToList.List>
						</SkuAddToList>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button variant="contained" onClick={handleSubmit}>
					確認
				</Button>
			</DialogActions>
		</Dialog>
	);
};

StoreTransferDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	refresh: PropTypes.func,
};

StoreTransferDialog.displayName = "StoreTransferDialog";

export default StoreTransferDialog;
